import React, { FC } from "react";
import { Card, Icon, Input, Label } from "@telia/teddy";
import { CopyButton } from "../../../components/atoms/CopyButton";
import { CustomerAddress, OrderDetails } from "../../../types/models";
import OrderDetailSection from "./OrderDetailSection";

interface CustomerSectionProps {
    order: OrderDetails;
}

interface CustomerFieldProps {
    id: string;
    label: string;
    value?: string | null;
    fullWidth?: boolean;
}

const formatAddress = (address?: CustomerAddress): string => {
    if (!address) return "";
    const streetNumber = address.streetNumber ? `${address.streetNumber}${address.streetNumberSuffix ?? ""}` : null;
    const streetLine = [address.streetName, streetNumber].filter(Boolean).join(" ");
    const postalLine = [address.zipCode, address.city].filter(Boolean).join(" ");
    return [streetLine, postalLine].filter(Boolean).join(", ");
};

const CustomerField: FC<CustomerFieldProps> = ({ id, label, value, fullWidth }) => (
    <div className={`order-detail__customer-field${fullWidth ? " order-detail__customer-field--full" : ""}`}>
        <Label htmlFor={id}>{label}</Label>
        <div className="order-detail__customer-field-row">
            <div className="order-detail__customer-field-input">
                <Input id={id} type="text" value={value ?? ""} readOnly data-di-mask />
            </div>
            <CopyButton value={value} ariaLabel={`Kopier ${label.toLowerCase()}`} toastMessage={`${label} kopiert`} />
        </div>
    </div>
);

const CustomerSection: FC<CustomerSectionProps> = ({ order }) => {
    const customer = order.customer;
    const addressLine = formatAddress(customer?.address);
    const isOrganization = customer?.type === "organization";
    const fullName = isOrganization
        ? (customer.organizationName ?? "")
        : [customer?.firstName, customer?.lastName].filter(Boolean).join(" ");

    return (
        <OrderDetailSection title={isOrganization ? "Eier (Bedrift)" : "Eier (Privat)"}>
            <Card variant="white" bordered className="order-detail__summary-card order-detail__summary-card--customer">
                <div className="order-detail__customer-card-heading">
                    <Icon name="end-user" size="md" aria-hidden="true" focusable="false" />
                    <Card.Heading variant="title-100" data-di-mask>
                        {fullName}
                    </Card.Heading>
                    <CopyButton value={fullName} ariaLabel="Kopier navn" toastMessage="Navn kopiert" />
                </div>
                <Card.Line />
                <Card.Content className="order-detail__customer-card-content">
                    <div className="order-detail__customer-grid">
                        {isOrganization ? (
                            <>
                                <CustomerField
                                    id="customer-organization-name"
                                    label="Bedriftsnavn"
                                    value={customer.organizationName}
                                />
                                <CustomerField
                                    id="customer-organization-number"
                                    label="Org. nummer"
                                    value={customer.organizationNumber}
                                />
                            </>
                        ) : (
                            <>
                                <CustomerField id="customer-first-name" label="Fornavn" value={customer?.firstName} />
                                <CustomerField id="customer-last-name" label="Etternavn" value={customer?.lastName} />
                                <CustomerField id="customer-email" label="E-post" value={customer?.email} />
                                <CustomerField
                                    id="customer-phone"
                                    label="Telefonnummer"
                                    value={customer?.phoneNumber}
                                />
                            </>
                        )}
                        <CustomerField id="customer-address" label="Adresse" value={addressLine} fullWidth />
                    </div>
                </Card.Content>
            </Card>
        </OrderDetailSection>
    );
};

export default CustomerSection;
